import { AddonGameplayData } from '@/components/addons/gameplay/Data';
import { GameSwipeitModel } from '@/components/games/swipeit/Model';
import { GameDiceModel } from '@/components/games/dice/Model';
import { GameShootItModel } from '@/components/games/shootit/Model';
import { GameSnakeModel } from '@/components/games/snake/Model';
import { GameSpinTheBottleModel } from '@/components/games/spinthebottle/Model';
import { GameWofModel } from '@/components/games/wof/Model';
import AddonModel from '@/models/grid/AddonModel';
import { AddonModelState } from '@/typings/interfaces/state/Addon';
import { GameRockPaperScissorsModel } from '@/components/games/rockpaperscissors/Model';
import { GameLuckyNumberModel } from '@/components/games/luckynumber/Model';
import { GameMemoryModel } from '@/components/games/memory/Model';
import { ConfigData } from '@/Config';
import useAxios from '@/hooks/useAxios';
import { InstantWinData } from '@/models/GameModel';
import store from '@/store';
import { CampaignModel } from '@/models/CampaignModel';
import { GameDropGameModel } from '@/components/games/dropgame/Model';
import { GameSpotTheDifferenceModel } from '@/components/games/spotthedifference/Model';
import { GamePredictionModel } from '@/components/games/prediction/Model';
import { GameGuessTheWordModel } from '@/components/games/guesstheword/Model';
import { GamePollModel } from '@/components/games/poll/Model';
import { GameHitTheTargetModel } from '@/components/games/hitthetarget/Model';
import { GameProductSelectorModel } from '@/components/games/product-selector/Model';
import { GameGuessThePictureModel } from '@/components/games/guessthepicture/Model';
import { GameShellModel } from '@/components/games/shell/Model';
import { GameSlidingPuzzleModel } from '@/components/games/sliding-puzzle/Model';
import { GameCampaignLinkModel } from '@/components/games/campaign-link/Model';
import { GamePersonalityTestModel } from '@/components/games/personality-test/Model';
import { GameSurveyModel } from '@/components/games/survey/Model';
import { GameQuizModel } from '@/components/games/quiz/Model';
import { GameCalendarModel } from '@/components/games/calendar/Model';
import { GamePriorityModel } from '@/components/games/priority/Model';
import { GameScratchCardModel } from '@/components/games/scratchcard/Model';
import { GamePlaceTheItemModel } from '@/components/games/placetheitem/Model';
import { GamePuzzleModel } from '@/components/games/puzzle/Model';
import { GameSlotModel } from '@/components/games/slot-machine/Model';
import { GameVideoQuizModel } from '@/components/games/video-quiz/Model';

export type ContentGameModel =
  | GameVideoQuizModel
  | GameSlotModel
  | GameGuessThePictureModel
  | GamePlaceTheItemModel
  | GameScratchCardModel
  | GameHitTheTargetModel
  | GameGuessTheWordModel
  | GamePredictionModel
  | GameSpotTheDifferenceModel
  | GameMemoryModel
  | GameLuckyNumberModel
  | GameSwipeitModel
  | GameDiceModel
  | GameShootItModel
  | GameSnakeModel
  | GameProductSelectorModel
  | GameSpinTheBottleModel
  | GameWofModel
  | GameSlidingPuzzleModel
  | GameRockPaperScissorsModel
  | GameShellModel
  | GameDropGameModel
  | GameCampaignLinkModel
  | GamePersonalityTestModel
  | GameQuizModel
  | GameCalendarModel
  | GameSurveyModel
  | GamePuzzleModel
  | GamePriorityModel;

export type ContentGameType =
  | typeof GameVideoQuizModel
  | typeof GameSlotModel
  | typeof GameGuessThePictureModel
  | typeof GamePollModel
  | typeof GamePlaceTheItemModel
  | typeof GameScratchCardModel
  | typeof GameHitTheTargetModel
  | typeof GameGuessTheWordModel
  | typeof GamePredictionModel
  | typeof GameSpotTheDifferenceModel
  | typeof GameMemoryModel
  | typeof GameLuckyNumberModel
  | typeof GameSwipeitModel
  | typeof GameDiceModel
  | typeof GameShootItModel
  | typeof GameSnakeModel
  | typeof GameProductSelectorModel
  | typeof GameSpinTheBottleModel
  | typeof GameWofModel
  | typeof GameSlidingPuzzleModel
  | typeof GameRockPaperScissorsModel
  | typeof GameShellModel
  | typeof GameDropGameModel
  | typeof GameCampaignLinkModel
  | typeof GamePersonalityTestModel
  | typeof GameQuizModel
  | typeof GameCalendarModel
  | typeof GameSurveyModel
  | typeof GamePuzzleModel
  | typeof GamePriorityModel;

const GameMapping: Record<string, ContentGameType> = {
  video_quiz: GameVideoQuizModel,
  slotmachine: GameSlotModel,
  guessthepicture: GameGuessThePictureModel,
  poll: GamePollModel,
  placetheitem: GamePlaceTheItemModel,
  scratchcard: GameScratchCardModel,
  hitthetarget: GameHitTheTargetModel,
  prediction: GamePredictionModel,
  guesstheword: GameGuessTheWordModel,
  finderrors: GameSpotTheDifferenceModel,
  memory: GameMemoryModel,
  luckynumber: GameLuckyNumberModel,
  dice: GameDiceModel,
  swipeit: GameSwipeitModel,
  shootit: GameShootItModel,
  snake: GameSnakeModel,
  spinthebottle: GameSpinTheBottleModel,
  wof: GameWofModel,
  rockpaperscissors: GameRockPaperScissorsModel,
  dropgame: GameDropGameModel,
  product_selector: GameProductSelectorModel,
  slidingpuzzle: GameSlidingPuzzleModel,
  shell: GameShellModel,
  campaign_link: GameCampaignLinkModel,
  personality_test: GamePersonalityTestModel,
  quiz: GameQuizModel,
  calendar: GameCalendarModel,
  survey: GameSurveyModel,
  puzzle: GamePuzzleModel,
  priority: GamePriorityModel
};

interface AddonGameplayState extends AddonModelState {
  settings?: {
    gameAlias: string;
    game?: ContentGameModel;
  };
  hasInstantWin: boolean;
}

export class AddonGameplayModel extends AddonModel<AddonGameplayData, AddonGameplayState> {
  parseAddon(data: AddonGameplayData): void {
    const state = this.state;
    state.alias = data.alias;

    if (data.settings?.game_alias) {
      if (!state.settings) {
        state.settings = {
          gameAlias: data.settings.game_alias
        };
      } else {
        state.settings.gameAlias = data.settings.game_alias;
      }

      if (typeof GameMapping[data.settings.game_alias] !== 'undefined') {
        if (state.settings.game && state.settings.game instanceof GameMapping[data.settings.game_alias]) {
          // Investigate if we're able to more strongly type this. Unfortunately i don't see any other way around this
          // due to data being different from game to game. Let's revisit this at a later point.
          // @ts-ignore
          state.settings.game.setData(data.settings.game);
        } else {
          // Investigate if we're able to more strongly type this. Unfortunately i don't see any other way around this
          // due to data being different from game to game. Let's revisit this at a later point.
          // @ts-ignore
          state.settings.game = new GameMapping[data.settings.game_alias](data.settings.game);
        }
      } else {
        state.settings.game = undefined;
      }
    } else {
      state.settings = undefined;
    }

    state.hasInstantWin = state.settings?.game ? state.settings.game.hasInstantWin() : false;
  }

  async fetchInstantWinData(): Promise<void> {
    const instantWinData = store.getters['global/getInstantWinData'] as InstantWinData | null;
    const campaignModel = store.getters['campaign/model'] as CampaignModel;
    const postUrl = `${ConfigData.apiEndpoint}/api/v1/campaign/action?campaign_id=${campaignModel.id}&action=auth`;

    const { postDataFormData } = useAxios<InstantWinData>(
      postUrl,
      instantWinData
        ? {
            ...instantWinData
          }
        : {}
    );

    try {
      const response = await postDataFormData();

      if (this.state.settings?.game && response) {
        this.state.settings.game.setInstantWinnerData(response ?? {});
      }
    } catch (e) {
      if (this.state.settings?.game) {
        this.state.settings.game.setInstantWinnerData({});
      }
    }
  }

  authorSignature(): string {
    return 'Sebastian Jakobsen | Dannie Hansen | Jannik Fischer';
  }
}
